import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";

import useCachedImageUrl from "../hooks/useCachedImageUrl";

/**
 * Drop-in replacement for <img> / MUI's component="img" pattern.
 *
 * Renders the image from its normal remote `src` immediately (so there's
 * never a blank gap waiting on our cache logic), then transparently swaps
 * to a Cache-Storage-backed blob: URL once one is ready. On a later visit —
 * same tab or a fresh reload — that swap happens near-instantly since the
 * image is already sitting in Cache Storage, no network round-trip needed.
 *
 * If Cache Storage isn't available or anything about the fetch/cache step
 * fails, it just keeps showing the original `src` — this component can
 * never make an image load worse than a plain <img> would.
 */
export default function CachedImage({ src, alt, sx, className, showSkeleton, ...rest }) {
  const resolvedSrc = useCachedImageUrl(src);
  const isFromCache = typeof resolvedSrc === "string" && resolvedSrc.startsWith("blob:");

  if (!isFromCache && showSkeleton) {
    return (
      <Box
        className={className}
        sx={{
          backgroundColor: "grey.200",
          animation: "cached-image-pulse 1.4s ease-in-out infinite",
          "@keyframes cached-image-pulse": {
            "0%, 100%": { opacity: 1 },
            "50%": { opacity: 0.6 },
          },
          ...sx,
        }}
      />
    );
  }

  return <Box component="img" src={resolvedSrc} alt={alt} className={className} sx={sx} {...rest} />;
}

CachedImage.propTypes = {
  /** Remote image URL — same as <img src>. */
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  sx: PropTypes.object,
  className: PropTypes.string,
  /** Show a pulsing placeholder instead of the network image while the
   * cache lookup resolves, instead of the (default) immediate network
   * image with a silent swap-to-cache afterwards. */
  showSkeleton: PropTypes.bool,
};

CachedImage.defaultProps = {
  alt: "",
  sx: {},
  className: undefined,
  showSkeleton: false,
};
